import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Upload, Plus, Search, FileText, ArrowUpDown, Calendar, Filter, X, BookOpen, PieChart, FolderPlus, Edit2, Trash2, BarChart3, List, RefreshCw, Printer } from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default categories
const DEFAULT_CATEGORIES = [
  'Supplier', 'Customer', 'Bank', 'Expense', 'Income', 
  'Asset', 'Liability', 'Capital A/C', 'Suspense', 'Other'
];

// Colors for charts
const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00C49F', '#FFBB28', '#FF8042', '#a4de6c'];

const Accounts = ({ user }) => {
  const [transactions, setTransactions] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [searchTerm, setSearchTerm] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [showLedgerForm, setShowLedgerForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showBalanceSheet, setShowBalanceSheet] = useState(false);
  const [balanceSheetView, setBalanceSheetView] = useState('table'); // 'table' or 'chart'
  const [showLedgerList, setShowLedgerList] = useState(false);
  const [editingLedger, setEditingLedger] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  // Sorting state - simple single sort by default
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [doubleSortEnabled, setDoubleSortEnabled] = useState(false);
  const [secondarySort, setSecondarySort] = useState({ key: null, direction: 'asc' });
  
  // Date Range Filter
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Ledger Filter
  const [selectedLedger, setSelectedLedger] = useState('');
  
  // Transaction Type Filter (Debit/Credit)
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all'); // 'all', 'Debit', 'Credit'
  
  // Inline Edit State
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const autocompleteRef = useRef(null);
  
  // Merge Ledger State
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergePrimary, setMergePrimary] = useState('');
  const [mergeSecondary, setMergeSecondary] = useState('');
  const [merging, setMerging] = useState(false);
  
  const [manualData, setManualData] = useState({
    date: '',
    amount: '',
    narration: '',
    transaction_type: 'Debit',
    balance: ''
  });
  
  const [ledgerData, setLedgerData] = useState({
    name: '',
    opening_balance: '',
    category: ''
  });

  const fetchTransactions = async () => {
    try {
      let url = `${API}/accounts/transactions`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', formatDateForAPI(startDate));
      if (endDate) params.append('end_date', formatDateForAPI(endDate));
      if (selectedLedger) params.append('ledger', selectedLedger);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };
  
  const fetchLedgers = async () => {
    try {
      const response = await axios.get(`${API}/ledgers`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setLedgers(response.data);
    } catch (error) {
      console.error('Failed to fetch ledgers');
    }
  };

  useEffect(() => {
    fetchTransactions();
    fetchLedgers();
  }, []);
  
  // Format date from YYYY-MM-DD to DD/MM/YYYY for API
  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };
  
  // Apply date filter
  const applyDateFilter = () => {
    fetchTransactions();
  };
  
  // Clear filters
  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedLedger('');
    setTransactionTypeFilter('all');
    setTimeout(() => fetchTransactions(), 100);
  };

  // Open print report in new tab
  const openPrintReport = () => {
    const reportData = {
      transactions: filteredAndSortedTransactions,
      filters: {
        startDate,
        endDate,
        selectedLedger,
        transactionTypeFilter
      },
      summary: {
        totalTransactions: filteredAndSortedTransactions.length,
        totalDebit: filteredAndSortedTransactions.filter(t => t.transaction_type === 'Debit').reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
        totalCredit: filteredAndSortedTransactions.filter(t => t.transaction_type === 'Credit').reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
      }
    };
    localStorage.setItem('accountsReportData', JSON.stringify(reportData));
    window.open('/accounts-report', '_blank');
  };

  // Simple sorting - with optional double sort mode
  const handleSort = (key) => {
    if (doubleSortEnabled && sortConfig.key && sortConfig.key !== key) {
      // Double sort mode: set as secondary sort
      setSecondarySort(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      }));
    } else {
      // Normal mode: set as primary sort
      setSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      }));
      // Clear secondary if clicking same key
      if (key === secondarySort.key) {
        setSecondarySort({ key: null, direction: 'asc' });
      }
    }
  };

  const clearSort = () => {
    setSortConfig({ key: null, direction: 'asc' });
    setSecondarySort({ key: null, direction: 'asc' });
    setDoubleSortEnabled(false);
  };

  const toggleDoubleSort = () => {
    if (doubleSortEnabled) {
      setSecondarySort({ key: null, direction: 'asc' });
    }
    setDoubleSortEnabled(!doubleSortEnabled);
  };

  const getSortIndicator = (key) => {
    const isPrimary = sortConfig.key === key;
    const isSecondary = secondarySort.key === key;
    
    if (isPrimary) {
      return (
        <span className="ml-1 inline-flex items-center">
          <span className={`${doubleSortEnabled ? 'bg-blue-600' : 'bg-slate-600'} text-white text-[10px] px-1.5 py-0.5 rounded font-bold`}>
            {doubleSortEnabled ? '1' : ''}{sortConfig.direction === 'asc' ? '↑' : '↓'}
          </span>
        </span>
      );
    }
    if (isSecondary && doubleSortEnabled) {
      return (
        <span className="ml-1 inline-flex items-center">
          <span className="bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded font-bold">
            2{secondarySort.direction === 'asc' ? '↑' : '↓'}
          </span>
        </span>
      );
    }
    return null;
  };

  // Compare function for transaction sorting
  const compareTransactions = (a, b, key, direction) => {
    const aVal = a[key];
    const bVal = b[key];

    if (key === 'date') {
      const parseDate = (dateStr) => {
        if (!dateStr) return new Date('1900-01-01');
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          return new Date(year, month, day);
        }
        return new Date(dateStr);
      };
      const aDate = parseDate(aVal);
      const bDate = parseDate(bVal);
      return direction === 'asc' ? aDate - bDate : bDate - aDate;
    }

    if (key === 'amount' || key === 'balance') {
      const aNum = parseFloat(aVal) || 0;
      const bNum = parseFloat(bVal) || 0;
      return direction === 'asc' ? aNum - bNum : bNum - aNum;
    }

    const aStr = String(aVal || '').toLowerCase();
    const bStr = String(bVal || '').toLowerCase();
    
    if (aStr < bStr) return direction === 'asc' ? -1 : 1;
    if (aStr > bStr) return direction === 'asc' ? 1 : -1;
    return 0;
  };

  const filteredAndSortedTransactions = useMemo(() => {
    let result = transactions;

    // Search filter
    if (searchTerm) {
      result = result.filter(t => 
        t.narration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.date?.includes(searchTerm) ||
        t.amount?.toString().includes(searchTerm) ||
        t.ledger?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Transaction Type Filter (Debit/Credit)
    if (transactionTypeFilter !== 'all') {
      result = result.filter(t => t.transaction_type === transactionTypeFilter);
    }

    // Sort with optional secondary
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        const primaryResult = compareTransactions(a, b, sortConfig.key, sortConfig.direction);
        
        if (primaryResult === 0 && doubleSortEnabled && secondarySort.key) {
          return compareTransactions(a, b, secondarySort.key, secondarySort.direction);
        }
        
        return primaryResult;
      });
    }

    return result;
  }, [transactions, searchTerm, sortConfig, secondarySort, doubleSortEnabled, transactionTypeFilter]);

  // Ledger inline edit handlers
  const handleLedgerDoubleClick = (transactionId, currentLedger) => {
    setEditingCell(transactionId);
    setEditValue(currentLedger || '');
    setShowAutocomplete(true);
  };
  
  // State for new ledger prompt
  const [pendingLedgerSave, setPendingLedgerSave] = useState(null); // { transactionId, ledgerName }
  const [showNewLedgerPrompt, setShowNewLedgerPrompt] = useState(false);
  
  const handleLedgerSave = async (transactionId) => {
    const trimmedValue = editValue.trim();
    if (!trimmedValue) {
      setEditingCell(null);
      setEditValue('');
      setShowAutocomplete(false);
      return;
    }
    
    // Check if ledger exists
    const ledgerExists = ledgers.some(l => l.name.toLowerCase() === trimmedValue.toLowerCase());
    
    if (!ledgerExists) {
      // Show prompt to create new ledger
      setPendingLedgerSave({ transactionId, ledgerName: trimmedValue });
      setShowNewLedgerPrompt(true);
      return;
    }
    
    // Ledger exists, save directly
    await saveLedgerToTransaction(transactionId, trimmedValue);
  };
  
  const saveLedgerToTransaction = async (transactionId, ledgerName) => {
    try {
      await axios.put(
        `${API}/accounts/transactions/${transactionId}/ledger?ledger_name=${encodeURIComponent(ledgerName)}`,
        {},
        { headers: { 'Authorization': `Bearer ${user.token}` } }
      );
      
      // Update local state
      setTransactions(prev => prev.map(t => 
        t.id === transactionId ? { ...t, ledger: ledgerName } : t
      ));
      
      setEditingCell(null);
      setEditValue('');
      setShowAutocomplete(false);
      toast.success(`Ledger "${ledgerName}" assigned`);
    } catch (error) {
      toast.error('Failed to save ledger');
    }
  };
  
  const handleCreateNewLedgerFromPrompt = () => {
    if (pendingLedgerSave) {
      // Store the transaction info before clearing
      const transactionId = pendingLedgerSave.transactionId;
      const ledgerName = pendingLedgerSave.ledgerName;
      
      // Open ledger creation modal with the name pre-filled
      setLedgerData({
        name: ledgerName,
        opening_balance: '',
        category: '',
        _pendingTransactionId: transactionId // Store transaction ID in ledger data
      });
      
      // Clear the prompt and pending state
      setShowNewLedgerPrompt(false);
      setPendingLedgerSave(null);
      setEditingCell(null);
      setEditValue('');
      setShowAutocomplete(false);
      
      // Show the ledger form
      setShowLedgerForm(true);
    }
  };
  
  const handleCancelNewLedgerPrompt = () => {
    // Cancel and clear
    setShowNewLedgerPrompt(false);
    setPendingLedgerSave(null);
    setEditingCell(null);
    setEditValue('');
    setShowAutocomplete(false);
  };
  
  const handleKeyDown = (e, transactionId) => {
    if (e.key === 'Enter') {
      handleLedgerSave(transactionId);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
      setShowAutocomplete(false);
    }
  };
  
  // Click outside to save
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (editingCell && autocompleteRef.current && !autocompleteRef.current.contains(e.target)) {
        handleLedgerSave(editingCell);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingCell, editValue]);
  
  // Autocomplete suggestions
  const ledgerSuggestions = useMemo(() => {
    if (!editValue) return ledgers.map(l => l.name);
    return ledgers
      .filter(l => l.name.toLowerCase().includes(editValue.toLowerCase()))
      .map(l => l.name);
  }, [editValue, ledgers]);

  const handleFileUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/accounts/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${user.token}`
        }
      });
      toast.success(response.data.message);
      
      if (response.data.file_id) {
        setUploadedFiles([...uploadedFiles, {
          id: response.data.file_id,
          name: file.name,
          uploaded_at: new Date().toLocaleString()
        }]);
      }
      
      setFile(null);
      document.getElementById('file-input').value = null;
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/accounts/manual-entry`, manualData, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Transaction added');
      setShowManualForm(false);
      setManualData({ date: '', amount: '', narration: '', transaction_type: 'Debit', balance: '' });
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to add transaction');
    }
  };
  
  const handleLedgerSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/ledgers`, {
        name: ledgerData.name,
        opening_balance: parseFloat(ledgerData.opening_balance) || 0,
        category: ledgerData.category
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Ledger created successfully');
      
      // If there's a pending transaction to assign this ledger to
      if (ledgerData._pendingTransactionId) {
        try {
          await axios.put(
            `${API}/accounts/transactions/${ledgerData._pendingTransactionId}/ledger?ledger_name=${encodeURIComponent(ledgerData.name)}`,
            {},
            { headers: { 'Authorization': `Bearer ${user.token}` } }
          );
          
          // Update local state
          setTransactions(prev => prev.map(t => 
            t.id === ledgerData._pendingTransactionId ? { ...t, ledger: ledgerData.name } : t
          ));
          
          toast.success(`Ledger "${ledgerData.name}" assigned to transaction`);
        } catch (error) {
          toast.error('Ledger created but failed to assign to transaction');
        }
      }
      
      setShowLedgerForm(false);
      setLedgerData({ name: '', opening_balance: '', category: '' });
      fetchLedgers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ledger');
    }
  };
  
  // Edit ledger
  const handleEditLedger = async (e) => {
    e.preventDefault();
    if (!editingLedger) return;
    
    try {
      await axios.put(`${API}/ledgers/${editingLedger.id}`, {
        name: editingLedger.name,
        opening_balance: parseFloat(editingLedger.opening_balance) || 0,
        category: editingLedger.category
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Ledger updated successfully');
      setEditingLedger(null);
      fetchLedgers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ledger');
    }
  };
  
  // Delete ledger
  const handleDeleteLedger = async (ledgerId, ledgerName) => {
    if (!window.confirm(`Are you sure you want to delete "${ledgerName}"?`)) return;
    
    try {
      await axios.delete(`${API}/ledgers/${ledgerId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Ledger deleted successfully');
      fetchLedgers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ledger');
    }
  };

  // Merge two ledgers
  const handleMergeLedgers = async () => {
    if (!mergePrimary || !mergeSecondary) {
      toast.error('Please select both ledgers to merge');
      return;
    }
    if (mergePrimary === mergeSecondary) {
      toast.error('Cannot merge a ledger with itself');
      return;
    }

    const primaryLedger = ledgers.find(l => l.id === mergePrimary);
    const secondaryLedger = ledgers.find(l => l.id === mergeSecondary);
    
    if (!window.confirm(`Merge "${secondaryLedger?.name}" into "${primaryLedger?.name}"?\n\nThis will:\n• Move all transactions from "${secondaryLedger?.name}" to "${primaryLedger?.name}"\n• Delete "${secondaryLedger?.name}" ledger\n\nThis action cannot be undone.`)) {
      return;
    }

    setMerging(true);
    try {
      await axios.post(`${API}/ledgers/merge`, {
        primary_ledger_id: mergePrimary,
        secondary_ledger_id: mergeSecondary
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      
      toast.success(`Successfully merged "${secondaryLedger?.name}" into "${primaryLedger?.name}"`);
      setShowMergeModal(false);
      setMergePrimary('');
      setMergeSecondary('');
      fetchLedgers();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge ledgers');
    } finally {
      setMerging(false);
    }
  };
  
  // Add new category
  const handleAddCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      setCategories([...categories, newCategory.trim()]);
      setLedgerData({ ...ledgerData, category: newCategory.trim() });
      setNewCategory('');
      setShowCategoryForm(false);
      toast.success('Category added');
    } else if (categories.includes(newCategory.trim())) {
      toast.error('Category already exists');
    }
  };
  
  // Calculate balance sheet summary by category
  const balanceSheetSummary = useMemo(() => {
    const summary = {};
    
    // Initialize categories
    categories.forEach(cat => {
      summary[cat] = { 
        totalDebit: 0, 
        totalCredit: 0, 
        netBalance: 0,
        ledgerCount: 0,
        ledgers: []
      };
    });
    
    // Add 'Uncategorized' for transactions without ledger
    summary['Uncategorized'] = { 
      totalDebit: 0, 
      totalCredit: 0, 
      netBalance: 0,
      ledgerCount: 0,
      ledgers: []
    };
    
    // Group ledgers by category
    ledgers.forEach(ledger => {
      const cat = ledger.category || 'Uncategorized';
      if (!summary[cat]) {
        summary[cat] = { totalDebit: 0, totalCredit: 0, netBalance: 0, ledgerCount: 0, ledgers: [] };
      }
      summary[cat].ledgerCount++;
      summary[cat].ledgers.push(ledger.name);
      summary[cat].netBalance += ledger.opening_balance || 0;
    });
    
    // Calculate totals from transactions
    transactions.forEach(t => {
      const ledgerName = t.ledger;
      const ledger = ledgers.find(l => l.name === ledgerName);
      const cat = ledger?.category || 'Uncategorized';
      
      if (!summary[cat]) {
        summary[cat] = { totalDebit: 0, totalCredit: 0, netBalance: 0, ledgerCount: 0, ledgers: [] };
      }
      
      if (t.transaction_type === 'Debit') {
        summary[cat].totalDebit += t.amount || 0;
      } else {
        summary[cat].totalCredit += t.amount || 0;
      }
    });
    
    // Calculate net for each category
    Object.keys(summary).forEach(cat => {
      summary[cat].netBalance = summary[cat].totalCredit - summary[cat].totalDebit + (summary[cat].netBalance || 0);
    });
    
    return summary;
  }, [transactions, ledgers, categories]);
  
  // Get unique ledgers from transactions for filter
  const uniqueLedgers = useMemo(() => {
    const ledgersFromTransactions = [...new Set(transactions.filter(t => t.ledger).map(t => t.ledger))];
    const allLedgers = [...new Set([...ledgers.map(l => l.name), ...ledgersFromTransactions])];
    return allLedgers.sort();
  }, [transactions, ledgers]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-lg sm:text-2xl font-bold text-slate-800">Bank Statements & Transactions</h2>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={() => { fetchTransactions(); fetchLedgers(); fetchCategories(); toast.success('Data refreshed'); }} 
            size="sm" 
            variant="outline" 
            className="text-slate-600 border-slate-300 hover:bg-slate-100"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button onClick={() => setShowBalanceSheet(true)} size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-50">
            <PieChart className="w-4 h-4 mr-2" />
            Balance Sheet
          </Button>
          <Button onClick={() => setShowLedgerList(true)} size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50">
            <List className="w-4 h-4 mr-2" />
            Manage Ledgers
          </Button>
          <Button onClick={() => setShowLedgerForm(!showLedgerForm)} size="sm" variant="outline" className="text-purple-600 border-purple-300 hover:bg-purple-50">
            <BookOpen className="w-4 h-4 mr-2" />
            New Ledger
          </Button>
          <Button onClick={() => setShowManualForm(!showManualForm)} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Manual Entry
          </Button>
        </div>
      </div>
      
      {/* Manage Ledgers Modal */}
      {showLedgerList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Manage Ledgers ({ledgers.length})</h3>
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  onClick={() => setShowMergeModal(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={ledgers.length < 2}
                >
                  Merge Ledgers
                </Button>
                <button onClick={() => { setShowLedgerList(false); setEditingLedger(null); }} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {ledgers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No ledgers created yet</p>
              ) : (
                <div className="space-y-2">
                  {ledgers.map(ledger => (
                    <div key={ledger.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50">
                      {editingLedger?.id === ledger.id ? (
                        <form onSubmit={handleEditLedger} className="space-y-3">
                          <Input
                            value={editingLedger.name}
                            onChange={(e) => setEditingLedger({ ...editingLedger, name: e.target.value })}
                            placeholder="Ledger Name"
                            required
                          />
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={editingLedger.opening_balance}
                              onChange={(e) => setEditingLedger({ ...editingLedger, opening_balance: e.target.value })}
                              placeholder="Opening Balance"
                              className="flex-1"
                            />
                            <select
                              value={editingLedger.category}
                              onChange={(e) => setEditingLedger({ ...editingLedger, category: e.target.value })}
                              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg"
                            >
                              <option value="">Select Category</option>
                              {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex gap-2">
                            <Button type="submit" size="sm" className="bg-green-600 hover:bg-green-700">Save</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => setEditingLedger(null)}>Cancel</Button>
                          </div>
                        </form>
                      ) : (
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-slate-800">{ledger.name}</div>
                            <div className="text-sm text-slate-500">
                              Opening: ₹{(ledger.opening_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              {ledger.category && (
                                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                  {ledger.category}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingLedger({ ...ledger })}
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteLedger(ledger.id, ledger.name)}
                              className="text-red-600 border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Merge Ledgers Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Merge Ledgers</h3>
              <button onClick={() => { setShowMergeModal(false); setMergePrimary(''); setMergeSecondary(''); }} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <strong>Note:</strong> All transactions from the secondary ledger will be moved to the primary ledger, and the secondary ledger will be deleted.
              </p>
              
              <div>
                <Label className="block mb-2 font-medium">Primary Ledger (Keep)</Label>
                <select
                  value={mergePrimary}
                  onChange={(e) => setMergePrimary(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select primary ledger...</option>
                  {ledgers.filter(l => l.id !== mergeSecondary).map(ledger => (
                    <option key={ledger.id} value={ledger.id}>{ledger.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="block mb-2 font-medium">Secondary Ledger (Merge & Delete)</Label>
                <select
                  value={mergeSecondary}
                  onChange={(e) => setMergeSecondary(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select secondary ledger...</option>
                  {ledgers.filter(l => l.id !== mergePrimary).map(ledger => (
                    <option key={ledger.id} value={ledger.id}>{ledger.name}</option>
                  ))}
                </select>
              </div>

              {mergePrimary && mergeSecondary && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-800">
                    <strong>"{ledgers.find(l => l.id === mergeSecondary)?.name}"</strong> will be merged into <strong>"{ledgers.find(l => l.id === mergePrimary)?.name}"</strong>
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleMergeLedgers}
                  disabled={!mergePrimary || !mergeSecondary || merging}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {merging ? 'Merging...' : 'Merge Ledgers'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowMergeModal(false); setMergePrimary(''); setMergeSecondary(''); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Balance Sheet Summary Modal */}
      {showBalanceSheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Balance Sheet Summary</h3>
              <div className="flex items-center gap-3">
                <div className="flex bg-white/20 rounded-lg p-1">
                  <button
                    onClick={() => setBalanceSheetView('table')}
                    className={`px-3 py-1 rounded text-sm ${balanceSheetView === 'table' ? 'bg-white text-green-700' : 'text-white/80 hover:text-white'}`}
                  >
                    <List className="w-4 h-4 inline mr-1" /> Table
                  </button>
                  <button
                    onClick={() => setBalanceSheetView('chart')}
                    className={`px-3 py-1 rounded text-sm ${balanceSheetView === 'chart' ? 'bg-white text-green-700' : 'text-white/80 hover:text-white'}`}
                  >
                    <BarChart3 className="w-4 h-4 inline mr-1" /> Charts
                  </button>
                </div>
                <button onClick={() => setShowBalanceSheet(false)} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
              {balanceSheetView === 'table' ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-3 px-2 font-semibold text-slate-700">Category</th>
                      <th className="text-right py-3 px-2 font-semibold text-slate-700">Ledgers</th>
                      <th className="text-right py-3 px-2 font-semibold text-green-600">Credit (₹)</th>
                      <th className="text-right py-3 px-2 font-semibold text-red-600">Debit (₹)</th>
                      <th className="text-right py-3 px-2 font-semibold text-slate-700">Net Balance (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(balanceSheetSummary)
                      .filter(([cat, data]) => data.ledgerCount > 0 || data.totalDebit > 0 || data.totalCredit > 0)
                      .map(([category, data]) => (
                      <tr key={category} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-2">
                          <span className="font-medium text-slate-800">{category}</span>
                          {data.ledgers.length > 0 && (
                            <div className="text-xs text-slate-500 mt-1">
                              {data.ledgers.slice(0, 3).join(', ')}
                              {data.ledgers.length > 3 && ` +${data.ledgers.length - 3} more`}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right text-slate-600">{data.ledgerCount}</td>
                        <td className="py-3 px-2 text-right text-green-600">
                          {data.totalCredit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-3 px-2 text-right text-red-600">
                          {data.totalDebit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className={`py-3 px-2 text-right font-semibold ${data.netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {data.netBalance >= 0 ? '' : '-'}₹{Math.abs(data.netBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td className="py-3 px-2 font-bold text-slate-800">TOTAL</td>
                      <td className="py-3 px-2 text-right font-bold text-slate-800">
                        {Object.values(balanceSheetSummary).reduce((sum, d) => sum + d.ledgerCount, 0)}
                      </td>
                      <td className="py-3 px-2 text-right font-bold text-green-600">
                        {Object.values(balanceSheetSummary).reduce((sum, d) => sum + d.totalCredit, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-2 text-right font-bold text-red-600">
                        {Object.values(balanceSheetSummary).reduce((sum, d) => sum + d.totalDebit, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className={`py-3 px-2 text-right font-bold ${Object.values(balanceSheetSummary).reduce((sum, d) => sum + d.netBalance, 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {Object.values(balanceSheetSummary).reduce((sum, d) => sum + d.netBalance, 0) >= 0 ? '' : '-'}
                        ₹{Math.abs(Object.values(balanceSheetSummary).reduce((sum, d) => sum + d.netBalance, 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="space-y-8">
                  {/* Pie Chart - Net Balance by Category */}
                  <div>
                    <h4 className="text-lg font-semibold text-slate-700 mb-4">Net Balance by Category</h4>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={Object.entries(balanceSheetSummary)
                              .filter(([cat, data]) => Math.abs(data.netBalance) > 0)
                              .map(([category, data], index) => ({
                                name: category,
                                value: Math.abs(data.netBalance),
                                isNegative: data.netBalance < 0
                              }))}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={({ name, value }) => `${name}: ₹${value.toLocaleString('en-IN')}`}
                            outerRadius={100}
                            dataKey="value"
                          >
                            {Object.entries(balanceSheetSummary)
                              .filter(([cat, data]) => Math.abs(data.netBalance) > 0)
                              .map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                          </Pie>
                          <Tooltip formatter={(value) => `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} />
                          <Legend />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  
                  {/* Bar Chart - Credit vs Debit */}
                  <div>
                    <h4 className="text-lg font-semibold text-slate-700 mb-4">Credit vs Debit by Category</h4>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={Object.entries(balanceSheetSummary)
                            .filter(([cat, data]) => data.totalCredit > 0 || data.totalDebit > 0)
                            .map(([category, data]) => ({
                              name: category,
                              Credit: data.totalCredit,
                              Debit: data.totalDebit
                            }))}
                          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                          <YAxis tickFormatter={(value) => `₹${(value/1000).toFixed(0)}K`} />
                          <Tooltip formatter={(value) => `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} />
                          <Legend />
                          <Bar dataKey="Credit" fill="#22c55e" name="Credit (₹)" />
                          <Bar dataKey="Debit" fill="#ef4444" name="Debit (₹)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Create Ledger Modal */}
      {showLedgerForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Create New Ledger</h3>
              <button onClick={() => setShowLedgerForm(false)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleLedgerSubmit} className="p-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Ledger Name *</Label>
                <Input
                  value={ledgerData.name}
                  onChange={(e) => setLedgerData({ ...ledgerData, name: e.target.value })}
                  placeholder="e.g., ELIKEM PHARMACEUTICALS"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Opening Balance</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={ledgerData.opening_balance}
                  onChange={(e) => setLedgerData({ ...ledgerData, opening_balance: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Category</Label>
                <div className="flex gap-2 mt-1">
                  <select
                    value={ledgerData.category}
                    onChange={(e) => setLedgerData({ ...ledgerData, category: e.target.value })}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowCategoryForm(true)}
                    className="px-3"
                    title="Add New Category"
                  >
                    <FolderPlus className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Add Category inline form */}
                {showCategoryForm && (
                  <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <Label className="text-xs font-medium text-slate-600">New Category Name</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        placeholder="e.g., Investment"
                        className="flex-1 text-sm"
                        autoFocus
                      />
                      <Button 
                        type="button" 
                        size="sm" 
                        onClick={handleAddCategory}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Add
                      </Button>
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="outline"
                        onClick={() => { setShowCategoryForm(false); setNewCategory(''); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowLedgerForm(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700">
                  Create Ledger
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Date Range & Ledger Filter Section */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-end">
          {/* Date Range */}
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="flex-1">
              <Label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                <Calendar className="w-4 h-4" /> From Date
              </Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="flex-1">
              <Label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                <Calendar className="w-4 h-4" /> To Date
              </Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
          
          {/* Ledger Filter */}
          <div className="flex-1 min-w-[200px]">
            <Label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
              <BookOpen className="w-4 h-4" /> Ledger
            </Label>
            <select
              value={selectedLedger}
              onChange={(e) => setSelectedLedger(e.target.value)}
              className="w-full h-9 px-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
            >
              <option value="">All Ledgers</option>
              {uniqueLedgers.map(l => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          
          {/* Transaction Type Filter */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
            <div className="flex gap-1">
              <Button 
                size="sm" 
                variant={transactionTypeFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setTransactionTypeFilter('all')}
                className={transactionTypeFilter === 'all' ? 'bg-slate-700' : ''}
              >
                All
              </Button>
              <Button 
                size="sm" 
                variant={transactionTypeFilter === 'Debit' ? 'default' : 'outline'}
                onClick={() => setTransactionTypeFilter('Debit')}
                className={transactionTypeFilter === 'Debit' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-300 hover:bg-red-50'}
              >
                Debit
              </Button>
              <Button 
                size="sm" 
                variant={transactionTypeFilter === 'Credit' ? 'default' : 'outline'}
                onClick={() => setTransactionTypeFilter('Credit')}
                className={transactionTypeFilter === 'Credit' ? 'bg-green-600 hover:bg-green-700' : 'text-green-600 border-green-300 hover:bg-green-50'}
              >
                Credit
              </Button>
            </div>
          </div>
          
          {/* Filter Buttons */}
          <div className="flex gap-2">
            <Button onClick={applyDateFilter} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
              <Filter className="w-4 h-4 mr-1" />
              Apply
            </Button>
            <Button onClick={openPrintReport} size="sm" className="bg-purple-600 hover:bg-purple-700">
              <Printer className="w-4 h-4 mr-1" />
              Print Report
            </Button>
            <Button onClick={clearFilters} size="sm" variant="outline">
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </div>
        </div>
        
        {/* Active Filters Display */}
        {(startDate || endDate || selectedLedger || transactionTypeFilter !== 'all') && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-slate-500">Active Filters:</span>
              {startDate && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                  From: {startDate}
                </span>
              )}
              {endDate && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                  To: {endDate}
                </span>
              )}
              {selectedLedger && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                  Ledger: {selectedLedger}
                </span>
              )}
              {transactionTypeFilter !== 'all' && (
                <span className={`px-2 py-1 rounded-full text-xs ${transactionTypeFilter === 'Debit' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  Type: {transactionTypeFilter}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* File Upload Section */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4">Upload Bank Statement</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Select File (PDF, Excel, CSV)</label>
            <input
              id="file-input"
              type="file"
              accept=".pdf,.xlsx,.xls,.csv"
              onChange={(e) => setFile(e.target.files[0])}
              className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 file:cursor-pointer cursor-pointer border border-slate-300 rounded-lg"
            />
          </div>
          {file && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-indigo-600 flex-shrink-0" />
                <span className="text-sm text-slate-700 break-all">{file.name}</span>
              </div>
              <Button onClick={handleFileUpload} disabled={uploading} size="sm" className="w-full sm:w-auto">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Manual Entry Form */}
      {showManualForm && (
        <form onSubmit={handleManualSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-4">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Add Transaction Manually</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
              <input
                type="date"
                value={manualData.date}
                onChange={(e) => setManualData({ ...manualData, date: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Amount</label>
              <input
                type="number"
                step="0.01"
                value={manualData.amount}
                onChange={(e) => setManualData({ ...manualData, amount: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Narration/Description</label>
            <input
              type="text"
              value={manualData.narration}
              onChange={(e) => setManualData({ ...manualData, narration: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
              <select
                value={manualData.transaction_type}
                onChange={(e) => setManualData({ ...manualData, transaction_type: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="Debit">Debit</option>
                <option value="Credit">Credit</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Balance (Optional)</label>
              <input
                type="number"
                step="0.01"
                value={manualData.balance}
                onChange={(e) => setManualData({ ...manualData, balance: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setShowManualForm(false)}>
              Cancel
            </Button>
            <Button type="submit">Save Transaction</Button>
          </div>
        </form>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search transactions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Transactions ({filteredAndSortedTransactions.length})
            </h3>
            {/* Double Sort Toggle Button */}
            <Button
              onClick={toggleDoubleSort}
              size="sm"
              variant={doubleSortEnabled ? "default" : "outline"}
              className={doubleSortEnabled ? "bg-purple-600 hover:bg-purple-700" : "text-purple-600 border-purple-300 hover:bg-purple-50"}
            >
              <ArrowUpDown className="w-4 h-4 mr-1" />
              {doubleSortEnabled ? "2-Sort ON" : "2-Sort"}
            </Button>
            {/* Sort Info & Clear */}
            {(sortConfig.key || secondarySort.key) && (
              <div className="flex items-center gap-2 text-xs">
                {sortConfig.key && (
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {doubleSortEnabled ? '1: ' : ''}{sortConfig.key} {sortConfig.direction === 'asc' ? '↑' : '↓'}
                  </span>
                )}
                {secondarySort.key && doubleSortEnabled && (
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    2: {secondarySort.key} {secondarySort.direction === 'asc' ? '↑' : '↓'}
                  </span>
                )}
                <button onClick={clearSort} className="text-slate-400 hover:text-red-500" title="Clear sort">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          <span className="text-sm text-slate-500">
            Showing {filteredAndSortedTransactions.length} of {transactions.length} transactions
          </span>
        </div>
        
        {/* Scrollable Table View */}
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center gap-1">
                    Date
                    {getSortIndicator('date') || <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Narration</th>
                <th 
                  className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Amount
                    {getSortIndicator('amount') || <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Type</th>
                <th 
                  className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('balance')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Balance
                    {getSortIndicator('balance') || <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('ledger')}
                >
                  <div className="flex items-center gap-1">
                    Ledger
                    {getSortIndicator('ledger') || <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredAndSortedTransactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{transaction.date}</td>
                  <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate" title={transaction.narration}>
                    {transaction.narration}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700 text-right whitespace-nowrap font-medium">
                    ₹{transaction.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      transaction.transaction_type === 'Credit' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {transaction.transaction_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700 text-right whitespace-nowrap">
                    ₹{transaction.balance?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td 
                    className="px-4 py-3 text-sm relative min-w-[150px]"
                    onDoubleClick={() => handleLedgerDoubleClick(transaction.id, transaction.ledger)}
                  >
                    {editingCell === transaction.id ? (
                      <div ref={autocompleteRef} className="relative">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, transaction.id)}
                          className="w-full px-2 py-1 border border-indigo-500 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoFocus
                          placeholder="Enter ledger name"
                        />
                        {showAutocomplete && ledgerSuggestions.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {ledgerSuggestions.map((suggestion, idx) => (
                              <div
                                key={idx}
                                className="px-3 py-2 text-sm hover:bg-indigo-50 cursor-pointer"
                                onClick={() => {
                                  setEditValue(suggestion);
                                  setShowAutocomplete(false);
                                }}
                              >
                                {suggestion}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span 
                        className={`cursor-pointer hover:bg-slate-100 px-2 py-1 rounded ${
                          transaction.ledger ? 'text-indigo-600 font-medium' : 'text-slate-400 italic'
                        }`}
                        title="Double-click to edit"
                      >
                        {transaction.ledger || 'Click to add'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {filteredAndSortedTransactions.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* New Ledger Prompt Modal */}
      {showNewLedgerPrompt && pendingLedgerSave && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Create New Ledger?
              </h3>
            </div>
            <div className="p-6">
              <p className="text-slate-700 mb-4">
                The ledger "<span className="font-bold text-indigo-600">{pendingLedgerSave.ledgerName}</span>" does not exist.
              </p>
              <p className="text-slate-600 mb-6">
                Would you like to create a new ledger with this name?
              </p>
              <div className="flex gap-3 justify-end">
                <Button 
                  onClick={handleCancelNewLedgerPrompt} 
                  variant="outline"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateNewLedgerFromPrompt}
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 px-6"
                >
                  Yes, Create Ledger
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default Accounts;
